import { Templates } from '@hellohair/types';
import { Box, Typography } from '@mui/material';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AddRowButton,
  FieldGroup,
  NextQuestionSelect,
  RepeatableRow,
  SearchIdSelect,
  StepField,
} from '../_shared/fields';
import { appendTo, removeAt, replaceAt } from '../_shared/stepData';
import { StepDialog, StepEditorProps } from '../_shared/StepDialog';
import { useStepDraft } from '../_shared/useStepDraft';

const SingleSelectAnswer: FC<StepEditorProps> = ({ survey, index, onClose }) => {
  const { t } = useTranslation();
  const { draft, set, save, dirty } = useStepDraft<Templates.StepDataSingle>(survey, index);
  const options = draft.options ?? [];

  const updateOption = (i: number, patch: Partial<Templates.SingleImageOption>) =>
    set('options', replaceAt(options, i, patch) as Templates.SingleOption[]);

  const addOption = (image: boolean) =>
    set(
      'options',
      appendTo(options, {
        type: image ? 'SingleSelectOptionImage' : 'SingleSelectOptionText',
        option: '',
        followerId: -1,
        ...(image ? { imageURL: '', imageLabel: '' } : {}),
      } as Templates.SingleOption)
    );

  return (
    <StepDialog
      survey={survey}
      index={index}
      onSave={save}
      onClose={onClose}
      dirty={dirty}
      edit={
        <>
          <StepField
            label={t('editor.field.question')}
            value={draft.question}
            onChange={(value) => set('question', value)}
            multiline
          />
          <StepField
            label={t('editor.field.extra')}
            value={draft.extra}
            onChange={(value) => set('extra', value)}
            multiline
          />
          <StepField
            label={t('editor.field.tip')}
            value={draft.tip}
            onChange={(value) => set('tip', value)}
            multiline
          />

          <FieldGroup title={t('editor.section.answers')}>
            {/*eslint-disable-next-line @typescript-eslint/no-explicit-any*/}
            {options.map((option: any, i: number) => (
              <RepeatableRow
                key={i}
                removeLabel={t('editor.action.removeAnswer')}
                onRemove={() => set('options', removeAt(options, i))}
              >
                <Box sx={{ flexGrow: 1, minWidth: '160px' }}>
                  <StepField
                    label={t('editor.field.answer')}
                    value={option.option}
                    onChange={(value) => updateOption(i, { option: value })}
                  />
                </Box>
                {option.type === 'SingleSelectOptionImage' && (
                  <Box sx={{ flexGrow: 1, minWidth: '160px' }}>
                    <StepField
                      label={t('editor.field.imageUrl')}
                      value={option.imageURL}
                      onChange={(value) => updateOption(i, { imageURL: value })}
                    />
                  </Box>
                )}
              </RepeatableRow>
            ))}
            <AddRowButton label={t('editor.add.answer')} onClick={() => addOption(false)} />
            <AddRowButton label={t('editor.add.imageAnswer')} onClick={() => addOption(true)} />
          </FieldGroup>
        </>
      }
      logic={
        options.length ? (
          <>
            {/*eslint-disable-next-line @typescript-eslint/no-explicit-any*/}
            {options.map((option: any, i: number) => (
              <Box key={i} sx={{ marginBottom: '4px' }}>
                <Typography variant="body2" fontWeight={600}>
                  {option.option || t('editor.field.answerPlaceholder', { number: i + 1 })}
                </Typography>
                <NextQuestionSelect
                  steps={survey.steps}
                  value={option.followerId}
                  onChange={(followerId) => updateOption(i, { followerId })}
                />
              </Box>
            ))}
          </>
        ) : (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t('editor.hint.noAnswersYet')}
          </Typography>
        )
      }
      settings={<SearchIdSelect value={draft.searchID} onChange={(searchID) => set('searchID', searchID)} />}
    />
  );
};

export default SingleSelectAnswer;
